import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SearchRounded from "@mui/icons-material/SearchRounded";
import DownloadRounded from "@mui/icons-material/DownloadRounded";
import MenuBookRounded from "@mui/icons-material/MenuBookRounded";
import { searchBookFiles } from "services/bookFiles";

export default function BookFilesAdmin() {
  const [code, setCode] = useState("");
  const [query, setQuery] = useState("");
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const search = async (event) => {
    event.preventDefault();
    if (loading || !code.trim()) return;
    setLoading(true);
    setError("");
    setBooks([]);
    setQuery(code.trim());
    try {
      setBooks(await searchBookFiles(code.trim()));
    } catch (failure) {
      setError(failure.message);
    } finally {
      setLoading(false);
    }
  };
  return (
    <Box className="book-files-workspace">
      <Typography className="files-eyebrow">BIBLIOTECA DE PRODUÇÃO</Typography>
      <Typography variant="h5" mb={1}>
        Arquivos de impressão
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Localize uma publicação pelo ISBN ou código PSO para baixar a capa e o miolo em PDF.
      </Typography>
      <Box component="form" onSubmit={search} className="files-search">
        <TextField
          label="ISBN ou código PSO"
          placeholder="Digite o código completo da obra"
          fullWidth
          value={code}
          onChange={(event) => setCode(event.target.value)}
          disabled={loading}
          inputProps={{ maxLength: 40 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRounded />
              </InputAdornment>
            ),
          }}
        />
        <Button
          type="submit"
          variant="contained"
          color="success"
          disabled={loading || !code.trim()}
          startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <SearchRounded />}
        >
          {loading ? "Pesquisando…" : "Pesquisar obra"}
        </Button>
      </Box>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      <Box aria-live="polite" aria-busy={loading}>
        {!loading && !error && books.length === 0 && (
          <Box className="files-empty">
            <MenuBookRounded sx={{ fontSize: 42, color: "#659a78", mb: 2 }} />
            <Typography variant="h6">
              {query
                ? "Nenhuma publicação encontrada"
                : "Encontre os arquivos da sua próxima impressão"}
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={1}>
              {query
                ? `Confira o ISBN ou código PSO “${query}” e faça uma nova pesquisa.`
                : "Pesquise pelo código completo. ISBN com espaços ou hífens também é aceito."}
            </Typography>
          </Box>
        )}
        {loading && (
          <Typography variant="body2" sx={{ py: 4 }}>
            Buscando a publicação e seus arquivos…
          </Typography>
        )}
        {!loading && books.length > 0 && (
          <Typography variant="body2" color="text.secondary" mb={2}>
            {books.length}{" "}
            {books.length === 1 ? "publicação encontrada" : "publicações encontradas"} para “{query}
            ”
          </Typography>
        )}
        {books.map((book) => (
          <Box component="article" className="files-book" key={book.id}>
            <Box className="files-thumbnail">
              {book.thumbnail ? (
                <Box
                  component="img"
                  src={book.thumbnail}
                  alt={`Capa de ${book.title}`}
                  onError={(event) => {
                    event.currentTarget.style.display = "none";
                  }}
                />
              ) : (
                <MenuBookRounded sx={{ fontSize: 48, color: "#86a18e" }} />
              )}
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Chip
                size="small"
                label={`PSO ${book.code}`}
                sx={{ mb: 1.5, bgcolor: "#edf6f0", color: "#147d48" }}
              />
              <Typography variant="h5" sx={{ overflowWrap: "anywhere" }}>
                {book.title || "Publicação sem título"}
              </Typography>
              <Typography variant="body2" color="text.secondary" mt={0.5}>
                {book.author || "Autor não informado"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                ISBN: {book.isbn || "Não informado"}
              </Typography>
              <Box className="files-specs">
                <Box>
                  <Typography variant="caption">Miolo</Typography>
                  <Typography variant="body2">
                    {book.pages ? `${book.pages} páginas` : "Paginação não informada"}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption">Formato do livro</Typography>
                  <Typography variant="body2">
                    {book.width && book.height
                      ? `${book.width} × ${book.height} cm`
                      : "Não informado"}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption">Papel do miolo</Typography>
                  <Typography variant="body2">
                    {book.paper || "Não informado"}
                    {book.grammage ? ` · ${book.grammage} g/m²` : ""}
                  </Typography>
                </Box>
              </Box>
              <Stack direction={{ xs: "column", sm: "row" }} gap={1.5} mt={2.5}>
                <Button
                  component={book.cover ? "a" : "button"}
                  href={book.cover || undefined}
                  variant="contained"
                  color="success"
                  disabled={!book.cover}
                  startIcon={<DownloadRounded />}
                >
                  {book.cover ? "Baixar capa · PDF" : "Capa indisponível"}
                </Button>
                <Button
                  component={book.interior ? "a" : "button"}
                  href={book.interior || undefined}
                  variant="outlined"
                  color="success"
                  disabled={!book.interior}
                  startIcon={<DownloadRounded />}
                >
                  {book.interior ? "Baixar miolo · PDF" : "Miolo indisponível"}
                </Button>
              </Stack>
              <Typography variant="caption" color="text.secondary" display="block" mt={1.5}>
                Links válidos por 15 minutos. Pesquise novamente para renová-los.
              </Typography>
              {(!book.cover || !book.interior) && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  Um ou mais PDFs não estão disponíveis para download. Confira os arquivos da
                  publicação ou a configuração de acesso ao armazenamento.
                </Alert>
              )}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
